/**
 * GraphQL POC Index Page
 *
 * Landing page showing links to enabled GraphQL client implementations.
 */

import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import LeftNav from 'components/navigation/LeftNav';
import { FocusGroup } from 'components/focus';
import Selectable from 'components/focus/Selectable';
import { useFocusManager } from 'hooks/focus';
import applicationActions from 'common/support/applicationActions';
import type { TypedFeaturesForUser } from 'support/fflip';
import type { VideoCtvRedux } from 'store-types';

import styles from './GraphQLPoc.module.scss';

interface ClientLink {
  featureKey: keyof TypedFeaturesForUser;
  path: string;
  name: string;
  description: string;
  recommendation: 'recommended' | 'alternative' | 'not-recommended';
}

const CLIENT_LINKS: ClientLink[] = [
  {
    featureKey: 'graphqlFetch',
    path: '/graphql-poc/fetch',
    name: 'Raw Fetch',
    description: 'Zero-dependency fetch wrapper (recommended)',
    recommendation: 'recommended',
  },
  {
    featureKey: 'graphqlGraphqlRequest',
    path: '/graphql-poc/graphql-request',
    name: 'graphql-request',
    description: 'Minimal GraphQL client (~5KB)',
    recommendation: 'alternative',
  },
  {
    featureKey: 'graphqlUrql',
    path: '/graphql-poc/urql',
    name: 'urql',
    description: 'Lightweight with caching (~12KB)',
    recommendation: 'alternative',
  },
  {
    featureKey: 'graphqlApollo',
    path: '/graphql-poc/apollo',
    name: 'Apollo Client',
    description: 'Full-featured client (~35KB)',
    recommendation: 'not-recommended',
  },
];

const badgeClassFor = (recommendation: ClientLink['recommendation']): string => {
  if (recommendation === 'recommended') return styles.badgeRecommended;
  if (recommendation === 'alternative') return styles.badgeAlternative;
  return styles.badgeNotRecommended;
};

interface GraphQLPocIndexProps {
  enabledFeatures: TypedFeaturesForUser;
}

const GraphQLPocIndex: React.FC<GraphQLPocIndexProps> = ({ enabledFeatures }) => {
  const history = useHistory();
  const focusManager = useFocusManager();

  // LeftNav hands focus to `${pageName}-focusable-content` on moveRight, so the
  // page's content group must own that exact id for the nav -> page handoff to
  // work (matches the Home/Settings convention).
  const pageName = useSelector(
    (state: VideoCtvRedux) => (state.pageView as { page?: string } | null)?.page,
  );
  const contentFocusId = `${(pageName ?? '').toLowerCase()}-focusable-content`;

  const enabledClients = CLIENT_LINKS.filter(client => enabledFeatures[client.featureKey]);

  // Place initial focus on the first available client link.
  useEffect(() => {
    if (enabledClients.length > 0) {
      focusManager.setCurrentFocus(`graphql-poc-link-0`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // moveLeft at the left edge hands focus to the LeftNav, matching the
  // client demo pages (HomeRows/SettingsRow pattern).
  const onPageActionOverride = useCallback(
    (action: string): boolean => {
      if (action === applicationActions.moveLeft) {
        focusManager.setCurrentFocus('left-nav-focusable-content');
        return true;
      }
      return false;
    },
    [focusManager],
  );

  return (
    <>
      <LeftNav focusId="left-nav-focusable-content" />
      <FocusGroup focusId={contentFocusId} onActionOverride={onPageActionOverride}>
        <div className={styles.container}>
          <h1 className={styles.title}>GraphQL POC</h1>
          <p className={styles.subtitle}>Persisted Operations with WunderGraph Cosmo</p>

          <div className={styles.clientList}>
            <h2>Available Client Implementations</h2>
            {enabledClients.length === 0 ? (
              <p>No GraphQL client implementations are currently enabled.</p>
            ) : (
              <ul className={styles.linkList}>
                {enabledClients.map((client, index) => (
                  <li key={client.path} className={styles.linkItem}>
                    <Selectable
                      focusId={`graphql-poc-link-${index}`}
                      onSelect={() => history.push(client.path)}
                      ttsText={`${client.name}, ${client.recommendation.replace('-', ' ')}`}
                      render={({ hasFocus }: { hasFocus: boolean }) => (
                        <div
                          className={classNames(
                            styles.clientLink,
                            hasFocus && styles.clientLinkFocused,
                          )}
                          onClick={() => history.push(client.path)}
                        >
                          <span className={styles.clientLinkName}>{client.name}</span>
                          <span className={classNames(styles.badge, badgeClassFor(client.recommendation))}>
                            {client.recommendation.replace('-', ' ')}
                          </span>
                          <span className={styles.clientLinkDesc}>{client.description}</span>
                        </div>
                      )}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.debugSection}>
            <div className={styles.debugTitle}>Feature Flags</div>
            <div className={styles.debugContent}>
              {JSON.stringify(
                {
                  graphqlFetch: enabledFeatures.graphqlFetch ?? false,
                  graphqlGraphqlRequest: enabledFeatures.graphqlGraphqlRequest ?? false,
                  graphqlUrql: enabledFeatures.graphqlUrql ?? false,
                  graphqlApollo: enabledFeatures.graphqlApollo ?? false,
                },
                null,
                2,
              )}
            </div>
          </div>
        </div>
      </FocusGroup>
    </>
  );
};

export default GraphQLPocIndex;
